import {UIEvent, useEffect, useRef, useState} from "react";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {Box, CircularProgress} from "@mui/material";
import {Message} from "@twilio/conversations";
import {map, throttle} from "lodash";

import {Colors, Container} from "@bounce/shared/ui";
import {MessageBubble} from "./MessageBubble";
import {AppState} from "../store/definitions";
import {getMoreMessages, setPreEngagementMessages} from "../store/actions/genericActions";
import {getDaysOld} from "../utils/getDaysOld";
import {MessageListSeparator} from "./MessageListSeparator";
import {TypingIndicator} from "./TypingIndicator";
import {MESSAGES_SPINNER_BOX_HEIGHT} from "../constants";

const isLastOfUserGroup = (message: Message, i: number, messages: Message[]) => {
  const nextMessage = messages[i + 1];

  if (!nextMessage) {
    return true;
  }

  return nextMessage.author !== message.author;
};

const isFirstOfDateGroup = (message: Message, i: number, messages: Message[]) => {
  const prevMessage = messages[i - 1];

  // if the previous message has a date older than the current message, this message is the first for this date
  // @ts-ignore
  return getDaysOld(prevMessage.dateCreated) > getDaysOld(message.dateCreated);
};

type MessageListProps = {
  isLoading?: boolean;
};

export const MessageList = ({isLoading = false}: MessageListProps) => {
  const {messages, participants, users, conversation, conversationsClient, preEngagementMessages} = useSelector(
    (state: AppState) => ({
      messages: state.chat.messages,
      participants: state.chat.participants,
      users: state.chat.users,
      conversation: state.chat.conversation,
      conversationsClient: state.chat.conversationsClient,
      preEngagementMessages: state.session.preEngagementMessages,
    }),
    shallowEqual
  );
  const dispatch = useDispatch();
  const messageListRef = useRef<HTMLDivElement>(null);
  const isLoadingMessages = useRef(false);
  const oldMessagesLength = useRef((messages || []).length);
  const [hasLoadedAllMessages, setHasLoadedAllMessages] = useState(true);

  const scrollToBottom = () => {
    if (!messageListRef.current) {
      return;
    }

    messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
  };

  useEffect(() => {
    const messageListener = () => {
      scrollToBottom();
    };

    conversation?.addListener("messageAdded", messageListener);

    return () => {
      conversation?.removeListener("messageAdded", messageListener);
    };
  }, [conversation]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (messages && messages.length > 0) {
      if (preEngagementMessages && preEngagementMessages.length > 0) {
        dispatch(setPreEngagementMessages([]));
      }
      setTimeout(scrollToBottom, 100);
    }
  }, [messages, preEngagementMessages, dispatch, isLoading]);

  useEffect(() => {
    const checkIfAllMessagesLoaded = async () => {
      const totalMessagesCount = await conversation?.getMessagesCount();
      if (totalMessagesCount) {
        setHasLoadedAllMessages(totalMessagesCount === messages?.length);
      }

      // if messages were added to state, loading is complete
      if (messages && oldMessagesLength.current < messages?.length) {
        isLoadingMessages.current = false;
        oldMessagesLength.current = messages.length;
      }
    };

    checkIfAllMessagesLoaded();
  }, [messages, conversation]);

  const handleScroll = async (event: UIEvent<HTMLDivElement>) => {
    const element = event.target as HTMLDivElement;
    const hasReachedTop =
      element.scrollHeight + element.scrollTop - MESSAGES_SPINNER_BOX_HEIGHT <= element.clientHeight;

    // When reaching the top of all messages, load the next chunk
    if (hasReachedTop && conversation && messages && !hasLoadedAllMessages && !isLoadingMessages.current) {
      isLoadingMessages.current = true;
      oldMessagesLength.current = messages.length;
      const totalMessagesCount = await conversation?.getMessagesCount();

      if (totalMessagesCount && messages.length < totalMessagesCount) {
        // @ts-ignore
        dispatch(getMoreMessages({anchor: totalMessagesCount - messages.length - 1, conversation}));
      }
    }
  };

  const renderSeparatorIfApplicable = (message: Message, i: number) => {
    const belongsToCurrentUser = message.author === conversationsClient?.user.identity;
    const isFirstUnreadMessage = message.index === (conversation?.lastReadMessageIndex as number) + 1;


    if (i > 0 && isFirstOfDateGroup(message, i, messages as Message[])) {
      return <MessageListSeparator message={message} separatorType="date" />;
    }


    if (isFirstUnreadMessage && !belongsToCurrentUser) {
      return <MessageListSeparator message={message} separatorType="new" />;
    }

    return null;
  };


  const renderPreEngagementMessages = () => {
    if (!preEngagementMessages?.length) {
      return null;
    }

    return preEngagementMessages.map((message, i) => (
      <Container key={message.id} sx={{gap: 0, alignItems: "stretch"}}>
        <MessageBubble
          message={message}
          isLast={i === preEngagementMessages.length - 1}
          isLastOfUserGroup={i === preEngagementMessages.length - 1 || preEngagementMessages[i + 1]?.author !== message.author}
        />
      </Container>
    ));
  };

  const renderChatItems = () => {
    if (isLoading) {
      return renderPreEngagementMessages();
    }

    if (!messages) {
      return null;
    }


    const spinnerIndex = (messages[0]?.index || 0) - 1;
    const messagesWithSpinner = [
      {
        index: spinnerIndex,
      } as Message,
      ...messages,
    ];

    return map(messagesWithSpinner,(message: Message, i: number) => {
      // First message in array represents the loading spinner
      if (message.index === spinnerIndex) {
        // Only render loading spinner if there are remaining messages to load
        return hasLoadedAllMessages ? null : (
          <Container
            row
            key={message.index}
            sx={{justifyContent: "center", height: `${MESSAGES_SPINNER_BOX_HEIGHT}px`, gap: 0}}
          >
            <CircularProgress size={24} sx={{color: Colors.grey._500}} aria-label="Loading" />
          </Container>
        );
      }
      // Discount loading spinner from indices
      i -= 1;

      return (
        <Container key={message.index} sx={{gap: 0, alignItems: "stretch"}}>
          {renderSeparatorIfApplicable(message, i)}
          <MessageBubble
            message={message}
            isLast={i === messages.length - 1}
            isLastOfUserGroup={isLastOfUserGroup(message, i, messages)}
          />
        </Container>
      );
    });
  };

  return (
    <Container
      sx={{
        flexGrow: 1,
        flexShrink: 1,
        minHeight: 0,
        justifyContent: "flex-end",
        overflowX: "hidden",
        flexDirection: "column-reverse",
        gap: 0,
      }}
    >
      <Box
        sx={{display: "flex", flex: 1, overflowY:"auto", width:"100%",margin:"12px"}}
        onScroll={throttle(handleScroll, 1000)}
        ref={messageListRef}
        role="main"
      >
        <Box
          aria-label="Chat messages"
          role="log"
          aria-relevant="additions"
          sx={{display: "flex", flexDirection: "column", p: "12px", width: "100%"}}
        >
          {renderChatItems()}
          {
            (isLoading ||
            participants?.some((p) => p.isTyping && p.identity !== conversationsClient?.user.identity))
            &&
            (
            <TypingIndicator />
          )
          }
        </Box>
      </Box>
    </Container>
  );
};
